import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface Theme {
  id: string;
  name: string;
  icon: string;
  colors: {
    mainBackground: string;
    textColor: string;
    boxesBackgroundColor: string;
    tableHeaderColor: string;
    boxesHeaderBackgroundColor: string;
    boxesHeaderBackgroundColorLight: string;
    errorColor: string;
    buttonColor: string;
    buttonHoverColor: string;
    placeholderColor: string;
    chartOrange: string;
    boxesBorder: string;
    alarmsListHoverColor: string;
    gradientColor: string;
    colorPrimary500: string;
    arrowIconColor: string;
    iconBackground: string;
    blackTextColor: string;
    invertIconFilter: string;
    borderColor: string;
    subtitleColor: string;
    menuBackground: string;
    dropdownBackgroundColor: string;
    filterBtnColor: string;
    selectionColor: string;
    filterSelection: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentThemeSubject = new BehaviorSubject<string>('dark');
  public currentTheme$ = this.currentThemeSubject.asObservable();

  private themes: Theme[] = [
    {
      id: 'light',
      name: 'Light',
      icon: 'sun-o',
      colors: {
        mainBackground: '#C8C8C8',
        textColor: '#000000',
        boxesBackgroundColor: '#E0E0E0',
        tableHeaderColor: '#333333',
        boxesHeaderBackgroundColor: '#9E9E9E',
        boxesHeaderBackgroundColorLight: '#8b8b94',
        errorColor: '#d32f2f',
        buttonColor: '#9E9E9E',
        buttonHoverColor: '#8b8b94',
        placeholderColor: '#333333',
        chartOrange: '#5f6466',
        boxesBorder: '#8b8b94',
        alarmsListHoverColor: '#E8E8E8',
        gradientColor: 'linear-gradient(#a9a9a9, #c0c0c0)',
        colorPrimary500: '#424151',
        arrowIconColor: '#000000',
        iconBackground: '#8b8b94',
        blackTextColor: '#ffffff',
        invertIconFilter: 'brightness(0) invert(0)',
        borderColor: '#000000',
        subtitleColor: '#7a7a7a',
        menuBackground: '#c0c0c0',
        dropdownBackgroundColor: '#9E9E9E',
        filterBtnColor: '#9E9E9E',
        selectionColor: '#9E9E9E',
        filterSelection: '#c0c0c0',
      }
    },
    {
      id: 'dark',
      name: 'Dark',
      icon: 'moon-o',
      colors: {
        mainBackground: '#101012',
        textColor: 'white',
        boxesBackgroundColor: '#1b1b25',
        tableHeaderColor: '#7d8a91',
        boxesHeaderBackgroundColor: '#272634',
        boxesHeaderBackgroundColorLight: '#7d8a91',
        errorColor: '#ff0000',
        buttonColor: '#37374f',
        buttonHoverColor: '#7d8a91',
        placeholderColor: '7d8a91',
        chartOrange: '#fac500',
        boxesBorder: '#57524c',
        alarmsListHoverColor: '#5e666b',
        gradientColor: 'linear-gradient(#424151, #535262)',
        colorPrimary500: '#424151',
        arrowIconColor: '#ffffff',
        iconBackground: '#fac500',
        blackTextColor: '#000000',
        invertIconFilter: 'brightness(0) invert(1)',
        borderColor: 'lightgray',
        subtitleColor: '#a4a4a4',
        menuBackground: '#ffffff',
        dropdownBackgroundColor: '#e8e8e8',
        filterBtnColor: '#37374f',
        selectionColor: '#37374f',
        filterSelection: '#55536d',
      }
    },
    {
      id: 'blue',
      name: 'Ocean Blue',
      icon: 'tint',
      colors: {
        mainBackground: '#172530',
        textColor: '#e8f4f8',
        boxesBackgroundColor: '#1f2e3f',
        tableHeaderColor: '#3879a3',
        boxesHeaderBackgroundColor: '#3a4f64',
        boxesHeaderBackgroundColorLight: '#7d8a91',
        errorColor: '#ff6b6b',
        buttonColor: '#3a4f64',
        buttonHoverColor: '#8a9ba8',
        placeholderColor: '#8a9ba8',
        chartOrange: '#e8e8e8',
        boxesBorder: '#7d7872',
        alarmsListHoverColor: '#254463',
        gradientColor: 'linear-gradient(#6a7f94, #4a5f74)',
        colorPrimary500: '#535262',
        arrowIconColor: '#FFFFFF',
        iconBackground: '#fac500',
        blackTextColor: '#000000',
        invertIconFilter: 'brightness(0) invert(1)',
        borderColor: 'lightgray',
        subtitleColor: '#a4a4a4',
        menuBackground: '#ffffff',
        dropdownBackgroundColor: '#e8e8e8',
        filterBtnColor: '#3a4f64',
        selectionColor: '#3a4f64',
        filterSelection: '#4a5f74',
      }
    },
    {
      id: 'grey',
      name: 'Modern Grey',
      icon: 'star',
      colors: {
        mainBackground: '#2a2a2a',
        textColor: '#ffffff',
        boxesBackgroundColor: '#4a4a4a',
        tableHeaderColor: '#e8e8e8',
        boxesHeaderBackgroundColor: '#5a5a62',
        boxesHeaderBackgroundColorLight: '#e8e8e8',
        errorColor: '#d32f2f',
        buttonColor: '#333333',
        buttonHoverColor: '#3a3a3e',
        placeholderColor: '#8b8b94',
        chartOrange: '#e8e8e8',
        boxesBorder: '#8b8b94',
        alarmsListHoverColor: '#5e666b',
        gradientColor: 'linear-gradient(#424151, #535262)',
        colorPrimary500: '#424151',
        arrowIconColor: '#FFFFFF',
        iconBackground: '#333333',
        blackTextColor: '#000000',
        invertIconFilter: 'brightness(0) invert(1)',
        borderColor: 'lightgray',
        subtitleColor: '#a4a4a4',
        menuBackground: '#ffffff',
        dropdownBackgroundColor: '#e8e8e8',
        filterBtnColor: '#333333',
        selectionColor: '#333333',
        filterSelection: '#5a5a62',
      }
    },
    // {
    //   id: 'navy',
    //   name: 'Midnight Blue',
    //   icon: 'shield',
    //   colors: {
    //     mainBackground: '#0f1419',
    //     textColor: '#e8eaed',
    //     boxesBackgroundColor: '#1a1f26',
    //     tableHeaderColor: '#5f7c8a',
    //     boxesHeaderBackgroundColor: '#2a3441',
    //     boxesHeaderBackgroundColorLight: '#3d4a5a',
    //     errorColor: '#e53935',
    //     buttonColor: '#2d3e50',
    //     buttonHoverColor: '#3d4f64',
    //     placeholderColor: '#6b7d8a',
    //     chartOrange: '#64b5f6',
    //     boxesBorder: '#3d4a5a',
    //     alarmsListHoverColor: '#25303d',
    //     gradientColor: 'linear-gradient(#2d3e50, #3d4f64)',
    //     colorPrimary500: '#2d3e50',
    //     arrowIconColor: '#FFFFFF',
    //     iconBackground: '#4a90a4',
    //     blackTextColor: '#000000',
    //     invertIconFilter: 'brightness(0) invert(1)',
    //     borderColor: '#4a5568',
    //     subtitleColor: '#9ca3af',
    //     menuBackground: '#ffffff',
    //     dropdownBackgroundColor: '#2a3441',
    //     filterBtnColor: '#2d3e50',
    //     selectionColor: '#3d4f64',
    //     filterSelection: '#4a5568',
    //   }
    // },
    // {
    //   id: 'neutral',
    //   name: 'Neutral',
    //   icon: 'leaf',
    //   colors: {
    //     mainBackground: '#D4C5B9',
    //     textColor: '#3E2723',
    //     boxesBackgroundColor: '#E5D9CF',
    //     tableHeaderColor: '#6D4C41',
    //     boxesHeaderBackgroundColor: '#BCAAA4',
    //     boxesHeaderBackgroundColorLight: '#C7B8AE',
    //     errorColor: '#8D6E63',
    //     buttonColor: '#8D6E63',
    //     buttonHoverColor: '#6D4C41',
    //     placeholderColor: '#8D6E63',
    //     chartOrange: '#A1887F',
    //     boxesBorder: '#A1887F',
    //     alarmsListHoverColor: '#C7B8AE',
    //     gradientColor: 'linear-gradient(#8D6E63, #6D4C41)',
    //     colorPrimary500: '#8D6E63',
    //     arrowIconColor: '#FFFFFF',
    //     iconBackground: '#8D6E63',
    //     blackTextColor: '#ffffff',
    //     invertIconFilter: 'brightness(0) invert(0)',
    //     borderColor: '#A1887F',
    //     subtitleColor: '#6D4C41',
    //     menuBackground: '#C7B8AE',
    //     dropdownBackgroundColor: '#C7B8AE',
    //     filterBtnColor: '#8D6E63',
    //     selectionColor: '#8D6E63',
    //     filterSelection: '#A1887F',
    //   }
    // }
  ];

  constructor() {
    // Load saved theme from localStorage
    const savedTheme = localStorage.getItem('uiTheme') || 'dark';
    this.setTheme(savedTheme);
  }

  getThemes(): Theme[] {
    return this.themes;
  }

  getCurrentTheme(): string {
    return this.currentThemeSubject.value;
  }

  setTheme(themeId: string): void {
    const theme = this.themes.find(t => t.id === themeId);
    if (theme) {
      this.currentThemeSubject.next(themeId);
      localStorage.setItem('uiTheme', themeId);
      this.applyTheme(theme);
    }
  }

  private applyTheme(theme: Theme): void {
    const root = document.documentElement;
    // Apply CSS custom properties directly to :root
    root.style.setProperty('--main_background', theme.colors.mainBackground);
    root.style.setProperty('--text_color', theme.colors.textColor);
    root.style.setProperty('--boxes_background_color', theme.colors.boxesBackgroundColor);
    root.style.setProperty('--table_header_color', theme.colors.tableHeaderColor);
    root.style.setProperty('--boxes_header_background_color', theme.colors.boxesHeaderBackgroundColor);
    root.style.setProperty('--boxes_header_background_color_light', theme.colors.boxesHeaderBackgroundColorLight);
    root.style.setProperty('--error-message-color', theme.colors.errorColor);
    root.style.setProperty('--button-color', theme.colors.buttonColor);
    root.style.setProperty('--button-hover-color', theme.colors.buttonHoverColor);
    root.style.setProperty('--placeholder-color', theme.colors.placeholderColor);
    root.style.setProperty('--chart-orange', theme.colors.chartOrange);
    root.style.setProperty('--boxes-border', theme.colors.boxesBorder);
    root.style.setProperty('--alarms-list-hover-color', theme.colors.alarmsListHoverColor);
    root.style.setProperty('--gradient-color', theme.colors.gradientColor);
    root.style.setProperty('--color-primary-500', theme.colors.colorPrimary500);
    root.style.setProperty('--arrow-icon-color', theme.colors.arrowIconColor);
    root.style.setProperty('--icon-background', theme.colors.iconBackground);
    root.style.setProperty('--black_text_color', theme.colors.blackTextColor);
    root.style.setProperty('--invert-icon-filter', theme.colors.invertIconFilter);
    root.style.setProperty('--border-color', theme.colors.borderColor);
    root.style.setProperty('--subtitle-color', theme.colors.subtitleColor);
    root.style.setProperty('--menu-background', theme.colors.menuBackground);
    root.style.setProperty('--dropdown-background-color', theme.colors.dropdownBackgroundColor);
    root.style.setProperty('--filter-btn-color', theme.colors.filterBtnColor);
    root.style.setProperty('--selection-color', theme.colors.selectionColor);
    root.style.setProperty('--filter-selection', theme.colors.filterSelection);
  }
}
